import { OpenPayService } from '../services/OpenPayService';
import { getAuth } from 'firebase/auth';
import Swal from 'sweetalert2';

const ODOO_BASE_URL = process.env.ODOO_BASE_URL || 'https://odoo.core.daisies.mx';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;

/**
 * Valida un cupón de descuento
 */
export const validateCoupon = async (couponCode, doctorId, userId, durationId, authToken) => {
  try {
    const payload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: 1,
        auth_token: authToken,
        res_id: 1,
        res_model: "dais.membership.order.coupon",
        res_method: "_check_validate_coupon_extra_consultation",
        res_params: {
          code: [couponCode.trim().toUpperCase()],
          doctor_id: parseInt(doctorId),
          user_id: parseInt(userId),
          duration_id: parseInt(durationId)
        }
      }
    };
    const response = await fetch(`${ODOO_BASE_URL}/odoo-firebase-core/odoo-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.data?.message || data.error.message || 'Error al validar cupón');
    }

    if (data.result?.success && data.result.data?.length > 0) {
      const couponData = data.result.data[0].coupon;
      return {
        success: true,
        coupon: {
          id: couponData.id,
          name: couponData.name,
          code: couponData.code,
          discountType: couponData.discount_type,
          discountAmount: couponData.discount_amount,
          discountPercentage: couponData.discount_percentage
        }
      };
    } else {
      throw new Error('Cupón no válido o expirado');
    }

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Obtiene un token de autenticación fresco de Firebase
 */
export const getFreshAuthToken = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
    }
    
    // Forzar refresh del token para obtener uno nuevo
    const token = await user.getIdToken(true);
    return token;
  } catch (error) {
    throw new Error('Error de autenticación. Por favor inicia sesión nuevamente.');
  }
};

/**
 * Tokeniza una tarjeta usando OpenPay
 */
export const tokenizeCard = async (cardData) => {
  try {
    await OpenPayService.loadScript();
    
    const deviceSessionId = OpenPayService.getDeviceSessionId();
    const tokenData = await OpenPayService.createToken(cardData);
    return {
      success: true,
      tokenData,
      deviceSessionId
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al tokenizar la tarjeta'
    };
  }
};

/**
 * Agrega una tarjeta como método de pago en Odoo
 */
export const addCardToOdoo = async (tokenData, deviceSessionId, cardOwnerName, authToken, userId) => {
  try {
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: 1,
        auth_token: authToken,
        res_model: "payment.processor",
        res_method: "add_user_payment_methods",
        res_id: 1,
        res_params: {
          is_default: true,
          user_id: parseInt(userId) || 0,
          tokenized_card: tokenData.id,
          card_owner_name: cardOwnerName.toUpperCase(),
          device_session_id: deviceSessionId
        }
      }
    };
    const response = await fetch(`${ODOO_BASE_URL}/odoo-firebase-core/odoo-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    // Verificar si hay error en la respuesta
    if (data.error) {
      throw new Error(data.error.data?.message || data.error.message || 'Error al agregar tarjeta');
    }

    if (data.result?.success) {
      // Validar que la respuesta contenga los datos de la tarjeta
      if (!data.result.data || !data.result.data.card_id) {
        throw new Error('La tarjeta se guardó pero no se recibió el ID. Por favor intenta de nuevo.');
      }
      
      return {
        success: true,
        cardId: data.result.data.card_id,
        paymentMethodId: data.result.data.payment_method_id,
        message: 'Tarjeta agregada exitosamente'
      };
    } else {
      throw new Error(data.result?.message || data.result?.error || 'Error al agregar tarjeta');
    }

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Procesa el pago de una consulta extra usando una tarjeta guardada
 */
export const payExtraConsultationWithCard = async (extraConsultationId, cardId, deviceSessionId, authToken, couponId = null) => {
  try {
    const payload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: 1,
        auth_token: authToken,
        res_model: "payment.processor",
        res_method: "pay_extra_consultation",
        res_id: 1,
        res_params: {
          extra_consultation_id: extraConsultationId,
          tokenized_card: "",
          card_id: cardId,
          coupon_id: couponId ? [couponId] : [],
          user_wom_wallet: false,
          wom_wallet_amount: 0,
          device_session_id: deviceSessionId,
          consultation_id: null
        }
      }
    };
    const response = await fetch(`${ODOO_BASE_URL}/odoo-firebase-core/odoo-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    // Verificar si hay error en la respuesta
    if (data.error) {
      throw new Error(data.error.data?.message || data.error.message || 'Error al procesar el pago');
    }

    if (data.result?.success) {
      return {
        success: true,
        transactionId: data.result.data?.transaction_id || cardId,
        apiResponse: data.result
      };
    } else {
      throw new Error(data.result?.message || data.result?.error || 'Error al procesar el pago');
    }

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Procesa el pago completo de una consulta individual
 * Flujo: tokenizar -> guardar tarjeta -> pagar con tarjeta guardada
 */
export const processConsultationPayment = async (cardData, consultationId, authToken, userId, couponId = null) => {
  try {
    // Obtener token fresco si no se proporciona uno
    const freshAuthToken = authToken || await getFreshAuthToken();
    
    // Validar datos de entrada
    if (!consultationId || !userId) {
      throw new Error('Faltan datos requeridos para procesar el pago');
    }

    // 1. Tokenizar tarjeta
    const tokenResult = await tokenizeCard(cardData);
    if (!tokenResult.success) {
      throw new Error(tokenResult.error);
    }

    const { tokenData, deviceSessionId } = tokenResult;

    // 2. Guardar tarjeta en Odoo
    const addCardResult = await addCardToOdoo(
      tokenData,
      deviceSessionId,
      cardData.name,
      freshAuthToken,
      userId
    );

    if (!addCardResult.success) {
      throw new Error(`Error al guardar tarjeta: ${addCardResult.error}`);
    }

    const { cardId } = addCardResult;
    // 3. Procesar pago con tarjeta guardada
    const paymentResult = await payExtraConsultationWithCard(
      consultationId,
      cardId,
      deviceSessionId,
      freshAuthToken,
      couponId
    );

    if (!paymentResult.success) {
      throw new Error(`Error al procesar pago: ${paymentResult.error}`);
    }
    return {
      success: true,
      transactionId: paymentResult.transactionId,
      cardId: cardId,
      cardLast4: cardData.number.slice(-4).replace(/\s/g, '')
    };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Valida un cupón para compra de membresía
 */
export const validateMembershipCoupon = async (couponCode, membershipId, userId, authToken) => {
  try {
    const freshToken = authToken || await getFreshAuthToken();
    const payload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: 1,
        auth_token: freshToken,
        res_id: 1,
        res_model: "dais.membership.order.coupon",
        res_method: "check_validate_coupon_by_membership",
        res_params: {
          code: [couponCode.trim().toUpperCase()],
          membership_id: parseInt(membershipId),
          user_id: parseInt(userId),
          marketplace_id: null
        }
      }
    };

    const response = await fetch(`${ODOO_BASE_URL}/odoo-firebase-core/odoo-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.data?.message || data.error.message || 'Error al validar cupón');
    }

    if (data.result?.success && data.result.data?.length > 0) {
      const couponData = data.result.data[0].coupon;
      return {
        success: true,
        coupon: {
          id: couponData.id,
          name: couponData.name,
          code: couponData.code,
          discountType: couponData.discount_type,
          discountAmount: couponData.discount_amount,
          discountPercentage: couponData.discount_percentage
        }
      };
    } else {
      throw new Error('Cupón no válido o expirado para esta membresía');
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Procesa el pago de una membresía con tarjeta guardada
 */
export const createMembershipPayment = async (membershipId, userId, cardId, deviceSessionId, authToken, couponIds = []) => {
  try {
    const freshToken = authToken || await getFreshAuthToken();
    const payload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: 1,
        auth_token: freshToken,
        res_model: "payment.processor",
        res_method: "create_membership_user_payment",
        res_id: 1,
        res_params: {
          membership_id: parseInt(membershipId),
          user_id: parseInt(userId),
          tokenized_card: cardId,
          card_id: cardId,
          coupon_id: couponIds,
          user_wom_wallet: false,
          wom_wallet_amount: 0,
          device_session_id: deviceSessionId
        }
      }
    };

    const response = await fetch(`${ODOO_BASE_URL}/odoo-firebase-core/odoo-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.data?.message || data.error.message || 'Error al procesar pago de membresía');
    }

    if (data.result?.success) {
      return {
        success: true,
        data: data.result.data,
        message: 'Membresía activada exitosamente'
      };
    } else {
      throw new Error(data.result?.message || data.result?.error || 'Error al procesar el pago de la membresía');
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Procesa el flujo completo de pago de membresía
 * Flujo: tokenizar → guardar tarjeta → pagar membresía
 */
export const processFullMembershipPayment = async (cardData, membershipId, userId, couponIds = []) => {
  try {
    const freshAuthToken = await getFreshAuthToken();

    if (!membershipId || !userId) {
      throw new Error('Faltan datos requeridos para procesar el pago');
    }

    // 1. Tokenizar tarjeta
    const tokenResult = await tokenizeCard(cardData);
    if (!tokenResult.success) {
      throw new Error(tokenResult.error);
    }

    const { tokenData, deviceSessionId } = tokenResult;

    // 2. Guardar tarjeta en Odoo
    const addCardResult = await addCardToOdoo(
      tokenData,
      deviceSessionId,
      cardData.name,
      freshAuthToken,
      userId
    );

    if (!addCardResult.success) {
      throw new Error(`Error al guardar tarjeta: ${addCardResult.error}`);
    }

    const { cardId } = addCardResult;

    // 3. Procesar pago de membresía
    const paymentResult = await createMembershipPayment(
      membershipId,
      userId,
      cardId,
      deviceSessionId,
      freshAuthToken,
      couponIds
    );

    if (!paymentResult.success) {
      throw new Error(`Error al procesar pago: ${paymentResult.error}`);
    }

    return {
      success: true,
      data: paymentResult.data,
      cardLast4: cardData.number.replace(/\s/g, '').slice(-4)
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Maneja la redirección a membresías
 */
export const redirectToMemberships = () => {
  window.location.href = 'https://app.daisies.mx';
};

/**
 * Muestra alertas de error de forma consistente
 */
export const showPaymentError = (message) => {
  Swal.fire({
    icon: 'error',
    title: 'Error en el pago',
    text: message,
    confirmButtonColor: '#786ED9'
  });
};

/**
 * Muestra alerta de éxito
 */
export const showPaymentSuccess = async () => {
  return Swal.fire({
    icon: 'success',
    title: '¡Pago Exitoso!',
    text: 'Tu pago ha sido procesado correctamente',
    showConfirmButton: false,
    timer: 2000
  });
};